import { useMutation, useQuery, useQueryClient } from "@tanstack/solid-query";

import { FetchError, fetchJSON, type LocationId, type PatientId } from "~/api";
import { coreFetch } from "~/request";
import { backgroundReload } from "~/inertia";
import {
  archiveLocationPatientTreatmentPresentationPath,
  askLocationPatientTreatmentPresentationPath,
  locationPatientTreatmentPresentationPath,
  locationPatientTreatmentPresentationsPath,
  locationTreatmentPresentationDrawingsPath,
  markupLocationPatientTreatmentPresentationSnapshotPath,
  unarchiveLocationPatientTreatmentPresentationPath,
} from "~/routes";
import type { MarkupChange } from "~/treatment_presentations/markup";
import type {
  AskQuestion,
  CaseSummary,
  TreatmentPresentationId,
  TreatmentPresentationSnapshotId,
} from "~/treatment_presentations/types";

const CASES_QUERY_KEY = "treatment-presentations";

/** The patient's cases at this office, for the chart's Cases mode. */
export function useCases(locationId: () => LocationId, patientId: () => PatientId) {
  return useQuery(() => ({
    queryKey: [CASES_QUERY_KEY, locationId(), patientId()],
    queryFn: () =>
      fetchJSON<{ cases: CaseSummary[] }>(
        locationPatientTreatmentPresentationsPath(locationId(), patientId()),
      ).then((answer) => answer.cases),
  }));
}

/** Clearing a presentation off the Presentations tab: Delete for one nobody saw yet, Archive (and Restore) once a
 *  version was sealed, since its versions are a record (DECISIONS.md 37). Each refreshes the list. */
export type CaseCleanup = "delete" | "archive" | "unarchive";

export function useCaseCleanup(locationId: () => LocationId, patientId: () => PatientId) {
  const queryClient = useQueryClient();
  return useMutation(() => ({
    mutationFn: (input: { id: TreatmentPresentationId; action: CaseCleanup }) => {
      const location = locationId();
      const patient = patientId();
      if (input.action === "delete")
        return fetchJSON<Record<string, never>>(
          locationPatientTreatmentPresentationPath(location, patient, input.id),
          {
            method: "DELETE",
          },
        );
      const path =
        input.action === "archive"
          ? archiveLocationPatientTreatmentPresentationPath(location, patient, input.id)
          : unarchiveLocationPatientTreatmentPresentationPath(location, patient, input.id);
      return fetchJSON<Record<string, never>>(path, { method: "PATCH" });
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: [CASES_QUERY_KEY, locationId(), patientId()] }),
  }));
}

/** A post that freezes the deck on screen (Present, the iPad code, the text's preview), sent with the revision on
 *  screen. A 409 means the saved deck moved on past that revision: the page is brought up to date, so staff check
 *  those slides before they try again, and the error still reaches the caller for its own message. */
export function sealDeck<Response>(
  path: string,
  body: { revision: number } & Record<string, unknown>,
): Promise<Response> {
  return fetchJSON<Response>(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).catch((err: unknown) => {
    if (err instanceof FetchError && err.status === 409)
      backgroundReload({ only: ["presentation"] });
    throw err;
  });
}

/** Ask Twelve for a change to the slides (slide: Redo with AI's one slide), or discard its proposal. Either way the
 *  page's props reload once the answer is back, carrying the proposal; nothing on the slides changes. */
/** => Twelve's question when it asks one before changing anything, else null once the proposal is on the page. */
export function askTwelve(
  ids: { locationId: LocationId; patientId: PatientId; presentationId: TreatmentPresentationId },
  body: { ask: string; revision: number; slide: string | null },
): Promise<AskQuestion | null> {
  return fetchJSON<Partial<AskQuestion>>(
    askLocationPatientTreatmentPresentationPath(ids.locationId, ids.patientId, ids.presentationId),
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
  ).then(({ question, choices }) => {
    if (question && choices) return { question, choices };
    backgroundReload({ only: ["presentation"] });
    return null;
  });
}

export function discardAsk(ids: {
  locationId: LocationId;
  patientId: PatientId;
  presentationId: TreatmentPresentationId;
}): Promise<void> {
  return fetchJSON<Record<string, never>>(
    askLocationPatientTreatmentPresentationPath(ids.locationId, ids.patientId, ids.presentationId),
    { method: "DELETE" },
  ).then(() => backgroundReload({ only: ["presentation"] }));
}

/** A screen's own drawing steps, saved onto the version's open layer (DECISIONS.md 53). `keepalive` lets the last
 *  save go out as the window closes. */
export function saveMarkup(
  ids: {
    locationId: LocationId;
    patientId: PatientId;
    snapshotId: TreatmentPresentationSnapshotId;
  },
  changes: MarkupChange[],
  keepalive = false,
): Promise<Response> {
  return coreFetch(
    markupLocationPatientTreatmentPresentationSnapshotPath(
      ids.locationId,
      ids.patientId,
      ids.snapshotId,
    ),
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ changes }),
      keepalive,
    },
  );
}

/** The office tablet's own drawing steps, under the start code's session rather than a sign-in. */
export function saveTabletMarkup(
  ids: { locationId: LocationId; snapshotId: TreatmentPresentationSnapshotId },
  changes: MarkupChange[],
  keepalive = false,
): Promise<Response> {
  return coreFetch(locationTreatmentPresentationDrawingsPath(ids.locationId, ids.snapshotId), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ changes }),
    keepalive,
  });
}
